/**
 * A deliberately small markdown renderer for the help wiki: exactly the
 * constructs docs/USER_GUIDE.md uses (headings, paragraphs, bold/italic/code,
 * links, nested lists, tables, blockquotes), rendered in the club's palette.
 * Cross-references like "section 5.6" become links to the matching article.
 */
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { resolveAnchor, resolveSection, slugify } from './guide';

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'quote'; paragraphs: string[] }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'list'; items: ListItem[]; ordered: boolean };

interface ListItem {
  text: string;
  extra: string[];
  children: { items: ListItem[]; ordered: boolean } | null;
}

const LIST_RE = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const isTableLine = (l: string) => /^\s*\|.*\|\s*$/.test(l);

function parseList(
  lines: string[],
  start: number,
): { list: { items: ListItem[]; ordered: boolean }; next: number } {
  const first = LIST_RE.exec(lines[start]!)!;
  const indent = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const items: ListItem[] = [];
  let i = start;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      // A blank line ends the list unless the next line continues it.
      const nextLine = lines[i + 1];
      const nextMatch = nextLine ? LIST_RE.exec(nextLine) : null;
      if (nextMatch && nextMatch[1]!.length >= indent) {
        i++;
        continue;
      }
      break;
    }
    const m = LIST_RE.exec(line);
    if (m) {
      const lineIndent = m[1]!.length;
      if (lineIndent < indent) break;
      if (lineIndent > indent) {
        const last = items[items.length - 1];
        if (!last) break;
        const nested = parseList(lines, i);
        last.children = nested.list;
        i = nested.next;
        continue;
      }
      items.push({ text: m[3]!, extra: [], children: null });
      i++;
      continue;
    }
    // Indented continuation text belongs to the previous item.
    if (/^\s{2,}\S/.test(line) && items.length > 0 && !isTableLine(line)) {
      items[items.length - 1]!.extra.push(line.trim());
      i++;
      continue;
    }
    break;
  }
  return { list: { items, ordered }, next: i };
}

function parseBlocks(markdown: string): Block[] {
  const lines = markdown.split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const t = line.trim();
    if (!t || /^-{3,}$/.test(t)) {
      i++;
      continue;
    }
    const heading = /^#{4,6}\s+(.+)$/.exec(t);
    if (heading) {
      blocks.push({ kind: 'heading', text: heading[1]!.trim() });
      i++;
      continue;
    }
    if (isTableLine(t)) {
      const rows: string[][] = [];
      while (i < lines.length && isTableLine(lines[i]!)) {
        const cells = lines[i]!.trim()
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((c) => c.trim());
        if (!cells.every((c) => /^:?-+:?$/.test(c))) rows.push(cells);
        i++;
      }
      blocks.push({ kind: 'table', rows });
      continue;
    }
    if (t.startsWith('>')) {
      const quoted: string[] = [];
      while (i < lines.length && lines[i]!.trim().startsWith('>')) {
        quoted.push(lines[i]!.trim().replace(/^>\s?/, ''));
        i++;
      }
      const paragraphs = quoted
        .join('\n')
        .split(/\n\s*\n/)
        .map((p) => p.replace(/\n/g, ' ').trim())
        .filter(Boolean);
      blocks.push({ kind: 'quote', paragraphs });
      continue;
    }
    if (LIST_RE.test(line)) {
      const { list, next } = parseList(lines, i);
      blocks.push({ kind: 'list', ...list });
      i = next;
      continue;
    }
    const buf = [t];
    i++;
    while (i < lines.length) {
      const n = lines[i]!.trim();
      if (!n || /^(#{1,6}\s|>|\||-{3,}$)/.test(n) || LIST_RE.test(lines[i]!)) break;
      buf.push(n);
      i++;
    }
    blocks.push({ kind: 'paragraph', text: buf.join(' ') });
  }
  return blocks;
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------

const INLINE_RE =
  /\*\*(.+?)\*\*|(?<![\w*])\*(?!\s)(.+?)\*(?!\w)|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s)*]+)|\b(sections?)\s+(\d+(?:\.\d+)?)/gu;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlight(text: string, terms: string[], keyBase: string): ReactNode {
  if (terms.length === 0) return text;
  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'giu');
  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={`${keyBase}-${i}`} className="rounded-sm bg-gold-400/30 text-cream-50">
        {part}
      </mark>
    ) : (
      <Fragment key={`${keyBase}-${i}`}>{part}</Fragment>
    ),
  );
}

function renderInline(text: string, terms: string[], keyBase = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  const re = new RegExp(INLINE_RE.source, 'gu');
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    const key = `${keyBase}-${n++}`;
    if (m.index > last)
      out.push(
        <Fragment key={`${key}-t`}>
          {highlight(text.slice(last, m.index), terms, `${key}-t`)}
        </Fragment>,
      );
    if (m[1] !== undefined) {
      out.push(
        <strong key={key} className="font-semibold text-cream-50">
          {renderInline(m[1], terms, key)}
        </strong>,
      );
    } else if (m[2] !== undefined) {
      out.push(<em key={key}>{renderInline(m[2], terms, key)}</em>);
    } else if (m[3] !== undefined) {
      out.push(
        <code
          key={key}
          className="rounded bg-ink-800/70 px-1 py-0.5 font-mono text-[0.85em] text-gold-200"
        >
          {m[3]}
        </code>,
      );
    } else if (m[4] !== undefined) {
      const href = m[5]!;
      const children = renderInline(m[4], terms, key);
      const internal = href.startsWith('#') ? resolveAnchor(href.slice(1)) : undefined;
      if (internal) {
        out.push(
          <Link
            key={key}
            to={internal}
            className="text-gold-200 underline-offset-2 hover:underline"
          >
            {children}
          </Link>,
        );
      } else if (/^https?:/.test(href)) {
        out.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-gold-200 underline-offset-2 hover:underline"
          >
            {children}
          </a>,
        );
      } else {
        out.push(<Fragment key={key}>{children}</Fragment>);
      }
    } else if (m[6] !== undefined) {
      out.push(
        <a
          key={key}
          href={m[6]}
          target="_blank"
          rel="noreferrer"
          className="text-gold-200 underline-offset-2 hover:underline"
        >
          {m[6]}
        </a>,
      );
    } else if (m[7] !== undefined) {
      const to = resolveSection(m[8]!);
      out.push(
        to ? (
          <Link key={key} to={to} className="text-gold-200 underline-offset-2 hover:underline">
            {m[7]} {m[8]}
          </Link>
        ) : (
          <Fragment key={key}>{highlight(m[0], terms, key)}</Fragment>
        ),
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length)
    out.push(
      <Fragment key={`${keyBase}-end`}>
        {highlight(text.slice(last), terms, `${keyBase}-end`)}
      </Fragment>,
    );
  return out;
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

function ListView({
  list,
  terms,
  keyBase,
}: {
  list: { items: ListItem[]; ordered: boolean };
  terms: string[];
  keyBase: string;
}) {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag
      className={`my-3 space-y-1.5 pl-6 ${list.ordered ? 'list-decimal' : 'list-disc'} marker:text-gold-400`}
    >
      {list.items.map((item, i) => (
        <li key={`${keyBase}-${i}`} className="pl-1">
          {renderInline(item.text, terms, `${keyBase}-${i}`)}
          {item.extra.map((e, j) => (
            <p key={j} className="mt-1 text-cream-200">
              {renderInline(e, terms, `${keyBase}-${i}-x${j}`)}
            </p>
          ))}
          {item.children && (
            <ListView list={item.children} terms={terms} keyBase={`${keyBase}-${i}c`} />
          )}
        </li>
      ))}
    </Tag>
  );
}

export function HelpMarkdown({
  source,
  highlightTerms = [],
}: {
  source: string;
  highlightTerms?: string[];
}) {
  const blocks = parseBlocks(source);
  const terms = highlightTerms;
  return (
    <div className="text-[15px] leading-relaxed text-cream-100">
      {blocks.map((block, b) => {
        const key = `b${b}`;
        switch (block.kind) {
          case 'heading': {
            const id = slugify(block.text);
            return (
              <h2
                key={key}
                id={id}
                className="mt-10 scroll-mt-24 border-b border-ink-800 pb-2 text-2xl text-gold-200 first:mt-0"
              >
                {renderInline(block.text, terms, key)}
              </h2>
            );
          }
          case 'paragraph':
            // Every screen and feature in the guide states its purpose; make it easy to spot.
            if (block.text.startsWith('**Purpose:**')) {
              return (
                <p
                  key={key}
                  className="my-3 rounded-md border border-gold-600/30 bg-ink-900/60 px-3 py-2 text-cream-200"
                >
                  {renderInline(block.text, terms, key)}
                </p>
              );
            }
            return (
              <p key={key} className="my-3">
                {renderInline(block.text, terms, key)}
              </p>
            );
          case 'quote':
            return (
              <div
                key={key}
                className="my-5 rounded-r-md border-l-4 border-gold-500 bg-gold-400/10 px-4 py-3 text-cream-100"
              >
                {block.paragraphs.map((p, i) => (
                  <p key={i} className="my-1">
                    {renderInline(p, terms, `${key}-${i}`)}
                  </p>
                ))}
              </div>
            );
          case 'table': {
            const [head, ...rows] = block.rows;
            return (
              <div key={key} className="my-5 overflow-x-auto rounded-md border border-ink-800">
                <table className="w-full border-collapse text-left text-sm">
                  {head && (
                    <thead className="bg-wine-700 text-on-wine">
                      <tr>
                        {head.map((cell, c) => (
                          <th key={c} scope="col" className="px-3 py-2 font-semibold">
                            {renderInline(cell.replace(/\*\*/g, ''), [], `${key}-h${c}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {rows.map((row, r) => (
                      <tr key={r} className="border-t border-ink-800 odd:bg-ink-900/40">
                        {head
                          ? head.map((_, c) => (
                              <td key={c} className="px-3 py-2 align-top">
                                {renderInline(row[c] ?? '', terms, `${key}-${r}-${c}`)}
                              </td>
                            ))
                          : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          case 'list':
            return <ListView key={key} list={block} terms={terms} keyBase={key} />;
        }
      })}
    </div>
  );
}
